// The element every ported screen has to render, and the first port of all of them missed.
//
// An AngularJS component has TWO nodes: the custom element the directive matches
// (`<chrome-extension-login-page>`) and its template root, which by convention is a div
// carrying the same name as a CLASS. Every page stylesheet targets both, and it puts the
// layout on the OUTER one:
//
//   chrome-extension-login-page { display: flex; flex: 1;
//     &.non-extended-ui { ... .chrome-extension-login-page { flex: 50% 0 0; } }
//     .chrome-extension-login-page { flex: 1; display: flex; flex-direction: column; }
//   }
//
// Render only the div — which is what a straight read of the .tpl.html leads you to do,
// since the element never appears in the template — and every outer rule is silently lost.
// The screen still looks broadly right, which is what makes it expensive: it shows up as a
// few percent of drifting pixels spread across every screen rather than as one broken page.
//
// Same lesson as `.chrome-extension-auth` in the abstract state (plan §7 phase 2 defect 1):
// in this codebase the DOM the stylesheet needs is not all in the template.
import { createElement, type ReactElement, type ReactNode } from 'react'

export interface PageHostProps {
  /**
   * The component's element name, e.g. `chrome-extension-login-page`. Used verbatim for BOTH
   * nodes: the custom element, and the class on the inner div — which is exactly how
   * upstream's selectors are written, so it cannot be split into two names.
   */
  name: string
  /** Extra classes on the inner div. Every auth page carries `popup-styles`. */
  className?: string
  /** Upstream toggles this on the HOST for the narrow frame widths. */
  hostClassName?: string
  children: ReactNode
}

export function PageHost({ name, className, hostClassName, children }: PageHostProps): ReactElement {
  // createElement, not JSX: a hyphenated tag in JSX needs an `IntrinsicElements` declaration
  // per page name, and there are twelve of them. React 19 passes unknown props straight
  // through to a custom element, so `class` is correct here rather than `className`.
  return createElement(
    name,
    hostClassName === undefined ? null : { class: hostClassName },
    <div className={[name, className].filter(Boolean).join(' ')}>{children}</div>,
  )
}
